import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { getLaboklinApi } from '../../../services/laboklinApi';
import { LABOKLIN_BORDER, LABOKLIN_RED } from '../../../theme/laboklinTheme';
import {
  BatchDetail,
  BatchHeader,
  ReportScore,
  gradeOf,
} from '../../../types/labConsistency';

/**
 * The batch: one row per (report, step), worst first.
 *
 * Verdict is the first column and the primary sort, because a split verdict outranks
 * any section score. A report that shows diagnoses on some runs and refuses on others
 * is a worse problem than one whose differential merely wobbles - both answers reached
 * a veterinarian as fact, and one of them was built on values the laboratory itself
 * called uninterpretable.
 */

const GRADE_COLOUR = {
  good: 'rgb(30, 130, 60)',
  warn: 'rgb(190, 130, 20)',
  bad: LABOKLIN_RED,
} as const;

const Score: React.FC<{ presence: number; content: number }> = ({ presence, content }) => (
  <Box sx={{ display: 'flex', gap: 0.75 }}>
    <Typography sx={{ fontSize: 12, fontWeight: 700, color: GRADE_COLOUR[gradeOf(presence, 'presence')] }}>
      {presence.toFixed(2)}
    </Typography>
    <Typography sx={{ fontSize: 12, color: GRADE_COLOUR[gradeOf(content, 'content')] }}>
      {content.toFixed(2)}
    </Typography>
  </Box>
);

export const ConsistencyBatchView: React.FC = () => {
  const { batchId } = useParams<{ batchId?: string }>();
  const navigate = useNavigate();
  const [batches, setBatches] = useState<BatchHeader[] | null>(null);
  const [detail, setDetail] = useState<BatchDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getLaboklinApi()
      .listConsistencyBatches()
      .then(({ batches: list }) => {
        setBatches(list);
        // No batch in the path: open the newest, which is what anyone arriving here wants.
        if (!batchId && list.length > 0) {
          navigate(`/mylab2/consistency-reports/${list[0].batchId}`, { replace: true });
        }
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [batchId, navigate]);

  useEffect(() => {
    if (!batchId) return;
    let live = true;
    setDetail(null);
    getLaboklinApi()
      .getConsistencyBatch(batchId)
      .then((d) => live && setDetail(d))
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [batchId]);

  const assessedCount = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of detail?.assessments ?? []) {
      const key = `${a.reportNo}#${a.step}`;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [detail]);

  const rows = useMemo(() => {
    const reports = [...(detail?.reports ?? [])];
    return reports.sort(
      (a, b) =>
        a.verdict.agreement - b.verdict.agreement ||
        worst(a) - worst(b) ||
        a.reportNo.localeCompare(b.reportNo)
    );
  }, [detail]);

  if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  if (!batches) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
  if (batches.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 3 }}>
        No consistency batch has been published yet.
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Typography sx={{ fontSize: 20, fontWeight: 600 }}>Consistency reports</Typography>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
        Each report was run several times through the real assistant. These numbers say only
        whether the answer came out the same each time — whether it is a good answer is what
        your assessment adds.
      </Typography>

      <Select
        size="small"
        value={batchId ?? batches[0].batchId}
        onChange={(e) => navigate(`/mylab2/consistency-reports/${e.target.value}`)}
        sx={{ mb: 2, minWidth: 320, fontSize: 13 }}
      >
        {batches.map((b) => (
          <MenuItem key={b.batchId} value={b.batchId} sx={{ fontSize: 13 }}>
            {b.label} · {new Date(b.generatedAt).toLocaleDateString()} · {b.runsPerReport} runs each
          </MenuItem>
        ))}
      </Select>

      {!detail ? (
        <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
      ) : (
        <Paper elevation={0} sx={{ border: `1px solid ${LABOKLIN_BORDER}`, borderRadius: 1, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Report', 'Step', 'Runs', 'Verdict', 'Diagnoses', 'Set aside', 'Follow-up labs', 'Assessed'].map((h) => (
                  <TableCell key={h} sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const split = r.verdict.agreement < 1;
                const allRefused = r.verdict.diagnostic === 0 && r.verdict.runs > 0;
                return (
                  <TableRow
                    key={`${r.reportNo}#${r.step}`}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/mylab2/consistency-reports/${r.batchId}/${r.reportNo}`)}
                  >
                    <TableCell sx={{ fontSize: 13, fontWeight: 500 }}>{r.reportNo}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{r.step}</TableCell>
                    {/*
                      Runs scored against runs attempted. A run that produced no case at
                      all is not scored - there are no panels to compare - so without this
                      the sections would report a flawless score over whichever runs
                      happened to survive. On one screening panel the assistant declined
                      to open a case on three runs of five, and the two that remained
                      agreed perfectly with each other.
                    */}
                    <TableCell>
                      <Typography sx={{ fontSize: 12, fontWeight: r.failedRuns > 0 ? 700 : 400, color: r.failedRuns > 0 ? LABOKLIN_RED : 'text.secondary' }}>
                        {r.runs - r.failedRuns}/{r.runs}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 12, fontWeight: split ? 700 : 400, color: split ? LABOKLIN_RED : 'text.secondary' }}>
                        {split
                          ? `split ${r.verdict.diagnostic}/${r.verdict.runs}`
                          : allRefused
                            ? `not diagnostic ${r.verdict.runs}/${r.verdict.runs}`
                            : 'diagnostic'}
                      </Typography>
                    </TableCell>
                    {/* Struck through where every run correctly refused: there is nothing
                        there to be inconsistent about, and a score would read as a failure. */}
                    {allRefused ? (
                      <TableCell colSpan={3} sx={{ fontSize: 12, color: 'text.disabled', fontStyle: 'italic' }}>
                        no differential, by design
                      </TableCell>
                    ) : (
                      <>
                        <TableCell><Score presence={r.differentials.presenceAgreement} content={r.differentials.contentAgreement} /></TableCell>
                        <TableCell><Score presence={r.setAside.presenceAgreement} content={r.setAside.contentAgreement} /></TableCell>
                        <TableCell><Score presence={r.tests.presenceAgreement} content={r.tests.contentAgreement} /></TableCell>
                      </>
                    )}
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {assessedCount.get(`${r.reportNo}#${r.step}`) ?? 0}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 1.5 }}>
        Each cell shows presence then content. Presence is whether the section appeared at all;
        content is whether it said the same thing when it did.
      </Typography>
    </Box>
  );
};

/** The lowest score on a row, used to sort the worst reports to the top. */
function worst(r: ReportScore): number {
  return Math.min(
    r.differentials.presenceAgreement,
    r.differentials.contentAgreement,
    r.setAside.presenceAgreement,
    r.setAside.contentAgreement,
    r.tests.presenceAgreement,
    r.tests.contentAgreement
  );
}

export default ConsistencyBatchView;
